/**
 *
 * Thermostat Model
 * @author Matt Shaffer, Chad Watson
 *
 *
 */
import { Record, OrderedMap, Seq } from "immutable";
import { add, ascend, prop } from "ramda";
import { clamp, isInRange, average } from "utils";
import zwaveDevicePropsFromJson, { defaultZwaveDeviceProps } from "./zwave";
export const FAN_MODES = {
  AUTO: "AUTO",
  ON: "ON"
};
export const MODES = {
  OFF: "OFF",
  COOL: "COOL",
  HEAT: "HEAT",
  AUTO: "AUTO"
};
export const SUBTYPES = {
  AUTO: "AUTO",
  STANDARD: "STANDARD"
};
export const TEMPERATURE_RANGES = {
  HEAT: {
    MIN: 1,
    MAX: 99
  },
  COOL: {
    MIN: 1,
    MAX: 99
  },
  AUTO: {
    MIN: 1,
    MAX: 99
  }
};
export const defaultCoolingPoint = average([
  TEMPERATURE_RANGES.COOL.MAX,
  TEMPERATURE_RANGES.COOL.MIN
]);
export const defaultHeatingPoint = average([
  TEMPERATURE_RANGES.HEAT.MAX,
  TEMPERATURE_RANGES.HEAT.MIN
]);
export const defaultAutoPoint = average([
  TEMPERATURE_RANGES.AUTO.MAX,
  TEMPERATURE_RANGES.AUTO.MIN
]);
export const validCoolingTemperature = isInRange(
  TEMPERATURE_RANGES.COOL.MIN,
  TEMPERATURE_RANGES.COOL.MAX
);
export const validHeatingTemperature = isInRange(
  TEMPERATURE_RANGES.HEAT.MIN,
  TEMPERATURE_RANGES.HEAT.MAX
);
export const validAutoTemperature = isInRange(
  TEMPERATURE_RANGES.AUTO.MIN,
  TEMPERATURE_RANGES.AUTO.MAX
);
export const getAutoCoolingPoint = add(-2);
export const getAutoHeatingPoint = add(2);
const createThermostat = Record({
  ...defaultZwaveDeviceProps,
  nodeType: "THERMOSTAT",
  currentTemperature: 0,
  setpointCooling: defaultCoolingPoint,
  setpointHeating: defaultHeatingPoint,
  fanMode: FAN_MODES.AUTO,
  mode: MODES.OFF
});

const createThermostatFromJson = json =>
  createThermostat({
    ...zwaveDevicePropsFromJson(json),
    currentTemperature: parseInt(json.current_temperature, 10),
    setpointCooling: clamp(
      json.setpoint_cooling,
      TEMPERATURE_RANGES.COOL.MIN,
      TEMPERATURE_RANGES.COOL.MAX
    ),
    setpointHeating: clamp(
      json.setpoint_heating,
      TEMPERATURE_RANGES.HEAT.MIN,
      TEMPERATURE_RANGES.HEAT.MAX
    ),
    fanMode: json.fan_mode,
    mode: json.mode
  });

export default createThermostatFromJson;
export const createThermostatsFromJson = json =>
  Seq(json)
    .map(createThermostatFromJson)
    .sort(ascend(prop("name")))
    .reduce(
      (acc, thermostat) => acc.set(thermostat.number, thermostat),
      OrderedMap()
    );
